import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Grid,
  Typography,
  Paper,
} from "@mui/material";
import { NotificationManager } from "react-notifications";
import { useNavigate, useParams } from "react-router-dom";
import CommonLoader from "../../common/CommonLoader";
import {
  SaveCoolerProfile,
  UpdateCoolerProfile,
  GetCoolerProfileById,
} from "../../../services/CoolerProfileService";

const AddCoolerProfile = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    profileName: "",
    coolerSize: "",
    shippingWeight: "",
    shippingWeightUnit: "lbs",
    shippingWidth: "",
    shippingHeight: "",
    shippingDepth: "",
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (id) {
      fetchProfileData(parseInt(id, 10));
    }
  }, [id]);

  const fetchProfileData = async (profileId) => {
    setLoading(true);

    try {
      const response = await GetCoolerProfileById(profileId);

      if (response?.results) {
        setFormData({
          ...response.results,
          shippingWeight:
            response.results.shippingWeight != null
              ? String(response.results.shippingWeight)
              : "",
          shippingWidth:
            response.results.shippingWidth != null
              ? String(response.results.shippingWidth)
              : "",
          shippingHeight:
            response.results.shippingHeight != null
              ? String(response.results.shippingHeight)
              : "",
          shippingDepth:
            response.results.shippingDepth != null
              ? String(response.results.shippingDepth)
              : "",
        });
      } else {
        NotificationManager.error(
          "Failed to fetch cooler profile data"
        );
      }
    } catch {
      NotificationManager.error(
        "Error fetching cooler profile data"
      );
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.profileName)
      newErrors.profileName = "Profile name is required";
    if (!formData.coolerSize)
      newErrors.coolerSize = "Cooler size is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const profileData = {
      ...formData,

      shippingWeight:
        formData.shippingWeight?.trim() !== ""
          ? String(formData.shippingWeight)
          : null,

      shippingWidth:
        formData.shippingWidth?.trim() !== ""
          ? String(formData.shippingWidth)
          : null,

      shippingHeight:
        formData.shippingHeight?.trim() !== ""
          ? String(formData.shippingHeight)
          : null,

      shippingDepth:
        formData.shippingDepth?.trim() !== ""
          ? String(formData.shippingDepth)
          : null,

      coolerProfileId: id ? parseInt(id, 10) : undefined,
    };

    setLoading(true);

    try {
      if (id) {
        await UpdateCoolerProfile(profileData);
        NotificationManager.success(
          "Cooler profile updated successfully"
        );
      } else {
        await SaveCoolerProfile(profileData);
        NotificationManager.success(
          "Cooler profile added successfully"
        );
      }

      navigate("/cooler-profiles");
    } catch (error) {
      NotificationManager.error(
        error.message || "Error saving cooler profile"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleCancel = () => navigate("/cooler-profiles");

  return (
    <>
      <Paper elevation={3} sx={{ padding: 3, margin: "auto" }}>
        <Typography variant="h5" gutterBottom>
          {id ? "Edit Cooler Profile" : "Add New Cooler Profile"}
        </Typography>

        <Grid container spacing={2}>
          {/* Identity */}
          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="Profile Name"
              value={formData.profileName}
              onChange={handleChange("profileName")}
              size="small"
              fullWidth
              required
              error={!!errors.profileName}
              helperText={errors.profileName}
            />
          </Grid>

          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="Cooler Size"
              value={formData.coolerSize || ""}
              onChange={handleChange("coolerSize")}
              size="small"
              fullWidth
              required
              error={!!errors.coolerSize}
              helperText={errors.coolerSize}
            />
          </Grid>

          {/* Shipping Weight */}
          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="Shipping Weight"
              type="number"
              value={formData.shippingWeight || ""}
              onChange={handleChange("shippingWeight")}
              size="small"
              fullWidth
            />
          </Grid>

          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="Weight Unit"
              value={formData.shippingWeightUnit || ""}
              onChange={handleChange("shippingWeightUnit")}
              size="small"
              fullWidth
            />
          </Grid>

          {/* Dimensions */}
          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="Shipping Width"
              type="number"
              value={formData.shippingWidth || ""}
              onChange={handleChange("shippingWidth")}
              size="small"
              fullWidth
            />
          </Grid>

          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="Shipping Height"
              type="number"
              value={formData.shippingHeight || ""}
              onChange={handleChange("shippingHeight")}
              size="small"
              fullWidth
            />
          </Grid>

          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="Shipping Depth"
              type="number"
              value={formData.shippingDepth || ""}
              onChange={handleChange("shippingDepth")}
              size="small"
              fullWidth
            />
          </Grid>

          {/* Actions */}
          <Grid item xs={12} container justifyContent="flex-end" spacing={2}>
            <Grid item>
              <Button onClick={handleCancel} variant="outlined" color="secondary">
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <Button onClick={handleSave} variant="contained" color="primary">
                {id ? "Update" : "Save"}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      <CommonLoader loading={loading} />
    </>
  );
};

export default AddCoolerProfile;